import type { CustomEvent as ObsrviqCustomEvent } from '@logclip/types';
import type { InstrumentCtx, Teardown } from './context.js';
import { uuid } from './util.js';

/**
 * Scroll-depth capture, per page path. Emits a `_scroll` event whenever the
 * visitor reaches a new, deeper 10% band of the page, so the server can take
 * each session's max depth per page. Cheap (a handful of events per page at
 * most) and value-free. The page identity is the pathname; depth resets on SPA
 * route changes.
 *
 * Each event also carries the page height `h` (full document height, px) and
 * viewport `vh` at that moment — so the server can tell apart "100% of a short
 * stub" from "100% of a long article", report absolute pixels reached, and flag
 * when a templated group blends pages of very different lengths.
 */
export function instrumentScrollDepth(ctx: InstrumentCtx): Teardown {
  let path = location.pathname;
  let maxBand = 0; // deepest 10% band reached on the current path
  let scheduled = false;

  const emit = (depth: number, h: number, vh: number) => {
    const e: ObsrviqCustomEvent = {
      id: uuid(),
      sessionId: ctx.sessionId,
      type: 'custom',
      t: ctx.clock.now(),
      ts: ctx.clock.wall(),
      name: '_scroll',
      props: { path, depth, h, vh },
    };
    ctx.emit(e);
  };

  const docHeight = (): number => {
    const d = document.documentElement;
    const b = document.body;
    return Math.max(d.scrollHeight, b ? b.scrollHeight : 0, d.clientHeight) || 1;
  };

  const measure = () => {
    scheduled = false;
    // Reset depth tracking when the SPA route changes.
    if (location.pathname !== path) {
      path = location.pathname;
      maxBand = 0;
    }
    const vh = window.innerHeight || document.documentElement.clientHeight || 1;
    const h = docHeight();
    const reached = Math.min(1, (window.scrollY + vh) / h);
    const band = Math.min(100, Math.round(reached * 10) * 10);
    if (band > maxBand) {
      maxBand = band;
      emit(band, h, vh);
    }
  };

  const onScroll = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(measure);
  };

  window.addEventListener('scroll', onScroll, { passive: true, capture: true });
  // Capture the entry fold immediately (a session that never scrolls still has a depth).
  measure();

  return () => {
    window.removeEventListener('scroll', onScroll, true);
  };
}
